// Settings page — currently hosts the "System Updates" panel.
// Admin-only actions (Apply Update) are gated by useRole().
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Progress,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CloudDownloadOutlined,
  DatabaseOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { InputNumber, Select } from "antd";
import { api, type GeoMMDBStatus, type LiveRecommendation, type LiveSettings, type UpdateCheck, type UpdateStatus } from "../api";
import { useRole } from "../contexts/AuthContext";
import { useGeoSettings } from "../contexts/GeoSettingsContext";
import APIKeysPanel from "../components/APIKeysPanel";
import APIConfigPanel from "../components/APIConfigPanel";
import LicensePanel from "../components/LicensePanel";

const { Title, Text, Paragraph } = Typography;

// ---------------------------------------------------------------------------
// LiveConfigPanel — live top-talker rolling window
// ---------------------------------------------------------------------------

// Time-range options for the dashboard chart default (must match the
// RANGES const in LiveChart.tsx — backend validates against this list).
const CHART_RANGE_OPTIONS = [
  { value: 5 * 60,           label: "Last 5 minutes" },
  { value: 10 * 60,          label: "Last 10 minutes" },
  { value: 15 * 60,          label: "Last 15 minutes" },
  { value: 60 * 60,          label: "Last Hour" },
  { value: 3 * 60 * 60,      label: "Last 3 Hours" },
  { value: 6 * 60 * 60,      label: "Last 6 Hours" },
  { value: 12 * 60 * 60,     label: "Last 12 Hours" },
  { value: 24 * 60 * 60,     label: "Last Day" },
  { value: 7 * 24 * 60 * 60, label: "Last Week" },
  { value: 30 * 24 * 60 * 60, label: "Last Month" },
];

// Auto-refresh interval options (ms). Mirrors REFRESHES in LiveChart.tsx.
const CHART_REFRESH_OPTIONS = [
  { value: 0,       label: "Off" },
  { value: 5_000,   label: "5 seconds" },
  { value: 30_000,  label: "30 seconds" },
  { value: 60_000,  label: "1 minute" },
  { value: 300_000, label: "5 minutes" },
];

function LiveConfigPanel() {
  const { isAdmin } = useRole();
  const [settings, setSettings]         = useState<LiveSettings>({
    window_secs: 60,
    window_mode: "mean",
    chart_default_range_secs: 3600,
    chart_default_refresh_ms: 5000,
  });
  // Original-loaded baseline. Used in save() to detect which fields the
  // user actually edited so we don't stomp concurrent writes from another
  // tab/admin (race observed when the form's Save button always shipped
  // the full body — saving Refresh would overwrite a freshly-set Window).
  const [original, setOriginal]         = useState<LiveSettings | null>(null);
  const [rec, setRec]                   = useState<LiveRecommendation | null>(null);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    Promise.all([
      api.liveSettings(),
      api.liveRecommendation().catch(() => null),
    ]).then(([s, r]) => {
      setSettings(s);
      setOriginal(s);
      setRec(r);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Refetch fresh server state and apply ONLY the fields the user
      // actually edited locally. This prevents a stale-state Save from
      // a long-open tab from overwriting a value another admin changed
      // in the meantime.
      const fresh = await api.liveSettings();
      const base = original ?? fresh;
      const merged: LiveSettings = {
        window_secs:
          settings.window_secs !== base.window_secs
            ? settings.window_secs
            : fresh.window_secs,
        window_mode:
          settings.window_mode !== base.window_mode
            ? settings.window_mode
            : fresh.window_mode,
        chart_default_range_secs:
          settings.chart_default_range_secs !== base.chart_default_range_secs
            ? settings.chart_default_range_secs
            : fresh.chart_default_range_secs,
        chart_default_refresh_ms:
          settings.chart_default_refresh_ms !== base.chart_default_refresh_ms
            ? settings.chart_default_refresh_ms
            : fresh.chart_default_refresh_ms,
      };
      const updated = await api.updateLiveSettings(merged);
      setSettings(updated);
      setOriginal(updated);
      message.success("Saved — live top settings updated");
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Determine hint: safe / warning / unknown
  const isBelowMin = rec != null && settings.window_secs < rec.min_safe_secs;

  // Protocol badge labels
  const protocolLabel: Record<string, string> = {
    sflow: "sFlow", nfv5: "NetFlow v5", nfv9: "NetFlow v9",
    ipfix: "IPFIX", auto: "auto",
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <ThunderboltOutlined />
          <span style={{ fontWeight: 700 }}>Live Top — Rolling Window</span>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 4 }}>
        Number of seconds to average bandwidth over for the live top
        talkers. A shorter window reacts faster to bursts but is more
        volatile. Minimum 60 s (sFlow) or 2× the NetFlow active-timeout,
        maximum 600 s.
      </Paragraph>
      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
        ⚡ <strong>Alert Rules use this window too</strong> — thresholds
        are compared against the averaged value over this same window. A
        shorter window lets brief bursts trigger an alert; a longer window
        requires <em>sustained</em> high traffic before it fires.
      </Paragraph>

      <Form layout="inline" disabled={loading || !isAdmin}>
        <Form.Item
          label="Mode"
          tooltip={
            <span>
              <b>Mean</b> — total bytes ÷ window. Smooth average; live
              drops to 0 during idle. Good for stable monitoring.<br />
              <b>Top-6</b> — mean of the 6 highest 5 s bins. Robust
              burst-detector: needs ~30 s of sustained traffic, ignores
              shorter noise spikes and NetFlow active-timeout bursts.
            </span>
          }
        >
          <Select
            value={settings.window_mode}
            onChange={(v) => setSettings({ ...settings, window_mode: v })}
            options={[
              { value: "mean", label: "Mean (smooth average)" },
              { value: "top6", label: "Top-6 (sustained burst, ~30s)" },
            ]}
            style={{ width: 260 }}
          />
        </Form.Item>

        <Form.Item label="Window (seconds)">
          <Space.Compact>
            <InputNumber
              min={60}
              max={600}
              step={30}
              value={settings.window_secs}
              onChange={(v) => setSettings({ ...settings, window_secs: v ?? 120 })}
              style={{ width: 100 }}
              status={isBelowMin ? "warning" : undefined}
            />
            <Button disabled style={{ cursor: "default", color: "var(--muted)", background: "var(--bg-1)" }}>
              s
            </Button>
          </Space.Compact>
        </Form.Item>

        <Form.Item
          label="Default Time Range"
          tooltip="Default time window for the Bits/s :: Global chart when a user first loads the dashboard."
        >
          <Select
            value={settings.chart_default_range_secs}
            onChange={(v) => setSettings({ ...settings, chart_default_range_secs: v })}
            options={CHART_RANGE_OPTIONS}
            style={{ width: 170 }}
          />
        </Form.Item>

        <Form.Item
          label="Default Refresh"
          tooltip="How often the chart auto-refreshes. Off = load once and stop. Use a low value (5s) for live NOC monitoring, higher (1m+) when reviewing long-term trends."
        >
          <Select
            value={settings.chart_default_refresh_ms}
            onChange={(v) => setSettings({ ...settings, chart_default_refresh_ms: v })}
            options={CHART_REFRESH_OPTIONS}
            style={{ width: 130 }}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            loading={saving}
            disabled={!isAdmin || loading}
            onClick={save}
          >
            Save
          </Button>
        </Form.Item>
      </Form>

      {/* Recommendation hint block */}
      {!loading && rec != null && (
        <div style={{ marginTop: 12 }}>
          {isBelowMin ? (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={
                <span>
                  Current window (<strong>{settings.window_secs}s</strong>) is below
                  the recommended safe minimum (<strong>{rec.min_safe_secs}s</strong>).{" "}
                  IPs may briefly drop out of the live top list and Alert Rules
                  can fire false positives because bandwidth values are more volatile.
                </span>
              }
              style={{ fontSize: 12, padding: "6px 10px" }}
            />
          ) : (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message={
                <span>
                  Window <strong>{settings.window_secs}s</strong> is within the safe range
                  (min: <strong>{rec.min_safe_secs}s</strong>)
                </span>
              }
              style={{ fontSize: 12, padding: "6px 10px" }}
            />
          )}
          <div style={{ marginTop: 8, fontSize: 11, color: "#8c8c8c", display: "flex", alignItems: "center", gap: 6 }}>
            <InfoCircleOutlined />
            <span>{rec.reason}</span>
            {rec.protocols.length > 0 && (
              <span style={{ marginLeft: 4 }}>
                — Protocols:{" "}
                {rec.protocols.map(p => (
                  <Tag key={p} style={{ fontSize: 10, padding: "0 4px", marginRight: 2 }}>
                    {protocolLabel[p] ?? p}
                  </Tag>
                ))}
              </span>
            )}
          </div>
        </div>
      )}

      {!isAdmin && (
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>Admin only.</Text>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// GeoSettingsPanel — IXP / NIX toggle card
// ---------------------------------------------------------------------------

function GeoSettingsPanel() {
  const { isAdmin } = useRole();
  const { settings, loading, loaded, reload } = useGeoSettings();
  const [saving, setSaving] = useState(false);

  const toggle = async (key: "ixp_enabled" | "nix_enabled", val: boolean) => {
    if (!isAdmin || !loaded) return;
    setSaving(true);
    try {
      await api.updateGeoSettings({ ...settings, [key]: val });
      await reload();
      message.success("Geo settings updated");
    } catch (e) {
      if (e instanceof Error) message.error("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <GlobalOutlined />
          <span style={{ fontWeight: 700 }}>Traffic Classification</span>
        </Space>
      }
      style={{ marginBottom: 16 }}
      loading={loading}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <Text strong>IXP — International Traffic</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Collect and display traffic to/from non-Vietnamese IP addresses.
              Disabling skips GeoIP lookup for international flows.
            </Text>
          </div>
          <Tooltip title={!isAdmin ? "Admin only" : !loaded ? "Loading…" : undefined}>
            <Switch
              checked={settings.ixp_enabled}
              disabled={!isAdmin || saving || !loaded}
              onChange={(v) => toggle("ixp_enabled", v)}
            />
          </Tooltip>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <Text strong>NIX — Domestic (VN) Traffic</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Collect and display traffic to/from Vietnamese IP addresses.
              Disabling skips GeoIP lookup for domestic flows.
            </Text>
          </div>
          <Tooltip title={!isAdmin ? "Admin only" : !loaded ? "Loading…" : undefined}>
            <Switch
              checked={settings.nix_enabled}
              disabled={!isAdmin || saving || !loaded}
              onChange={(v) => toggle("nix_enabled", v)}
            />
          </Tooltip>
        </div>
      </div>

      {!isAdmin && (
        <Alert
          type="info"
          showIcon
          message="These settings can only be changed by an admin."
          style={{ marginTop: 12 }}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// GeoIPUpdatePanel — MaxMind GeoLite2 download + auto-update config
// ---------------------------------------------------------------------------

function fmtBytes(n: number): string {
  if (n === 0) return "—";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function GeoIPUpdatePanel() {
  const { isAdmin } = useRole();
  const [status, setStatus]   = useState<GeoMMDBStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [updating, setUpdating] = useState(false);

  const [form] = Form.useForm();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const s = await api.geoMMDBStatus();
      setStatus(s);
      return s;
    } catch { /* ignore */ }
    return null;
  }, []);

  // Poll while an update is in flight.
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const s = await fetchStatus();
      if (s && !s.updating) {
        clearInterval(pollRef.current!);
        pollRef.current = null;
        setUpdating(false);
        if (s.last_error) {
          message.error("GeoIP update failed: " + s.last_error);
        } else {
          message.success("GeoIP database updated successfully!");
        }
      }
    }, 2500);
  }, [fetchStatus]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchStatus();
      setLoading(false);
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchStatus]);

  const handleSave = async (vals: { license_key?: string; auto_update: boolean }) => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const s = await api.updateGeoMMDB({
        license_key: vals.license_key || undefined,
        auto_update: vals.auto_update,
      });
      setStatus(s);
      form.setFieldsValue({ license_key: "", auto_update: s.auto_update });
      message.success("GeoIP settings saved");
    } catch (e) {
      if (e instanceof Error) message.error("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateNow = async () => {
    if (!isAdmin) return;
    setUpdating(true);
    try {
      await api.triggerGeoMMDBUpdate();
      message.info("Download started — checking progress…");
      startPolling();
    } catch (e) {
      setUpdating(false);
      if (e instanceof Error) message.error(e.message);
    }
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <DatabaseOutlined />
          <span style={{ fontWeight: 700 }}>GeoIP Database (GeoLite2)</span>
          {status && !status.license_key_set && (
            <Tag color="orange">Key not set</Tag>
          )}
          {status && (
            status.mmdb_bytes > 0
              ? <Tag color="green">Country {fmtBytes(status.mmdb_bytes)}</Tag>
              : <Tag color="red">Country missing</Tag>
          )}
          {status && (
            status.mmdb_asn_bytes > 0
              ? <Tag color="green">ASN {fmtBytes(status.mmdb_asn_bytes)}</Tag>
              : <Tag color="red">ASN missing</Tag>
          )}
        </Space>
      }
      style={{ marginBottom: 16 }}
      loading={loading}
    >
      {/* Status row */}
      {status && (
        <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Last updated">
            {status.last_updated
              ? new Date(status.last_updated).toLocaleString()
              : <span style={{ color: "#999" }}>Never</span>}
          </Descriptions.Item>
          <Descriptions.Item label="Auto-update">
            {status.auto_update
              ? <Tag color="blue">Weekly (Wed 01:00 UTC)</Tag>
              : <Tag color="default">Off</Tag>}
          </Descriptions.Item>
          {status.last_error && (
            <Descriptions.Item label="Last error" span={2}>
              <span style={{ color: "#cf1322", fontSize: 12 }}>{status.last_error}</span>
            </Descriptions.Item>
          )}
        </Descriptions>
      )}

      {/* Info box */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="MaxMind GeoLite2 — free account required"
        description={
          <span>
            Sign up for a free account at{" "}
            <a href="https://www.maxmind.com/en/geolite2/signup" target="_blank" rel="noreferrer">
              maxmind.com
            </a>
            {" "}→ My Account → Manage License Keys → Generate key.
            MaxMind publishes updated databases every Tuesday; the system will automatically download both Country and ASN databases every Wednesday when Auto-update is enabled.
          </span>
        }
      />

      {/* Config form */}
      {isAdmin ? (
        <Form
          form={form}
          layout="vertical"
          initialValues={{ auto_update: status?.auto_update ?? false }}
          onFinish={handleSave}
          style={{ maxWidth: 540 }}
        >
          <Form.Item
            name="license_key"
            label={
              <span>
                MaxMind License Key
                {status?.license_key_set && (
                  <Tag color="green" style={{ marginLeft: 8 }}>Saved</Tag>
                )}
              </span>
            }
            help={status?.license_key_set ? "Leave blank to keep the current key unchanged." : undefined}
          >
            <Input.Password
              placeholder={status?.license_key_set ? "••••••••••••••••" : "Enter license key"}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item name="auto_update" valuePropName="checked" label="Weekly auto-update">
            <Switch checkedChildren="On" unCheckedChildren="Off" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                Save settings
              </Button>
              <Tooltip title={!status?.license_key_set ? "License key required" : ""}>
                <Button
                  icon={updating ? <LoadingOutlined spin /> : <SyncOutlined />}
                  onClick={handleUpdateNow}
                  loading={updating}
                  disabled={!status?.license_key_set || updating}
                >
                  Update now
                </Button>
              </Tooltip>
            </Space>
          </Form.Item>
        </Form>
      ) : (
        <Alert type="info" showIcon message="Only admins can change GeoIP configuration." />
      )}

      {/* In-flight progress */}
      {(updating || status?.updating) && (
        <div style={{ marginTop: 12 }}>
          <Progress percent={0} status="active" size="small" />
          <Tag color="blue" icon={<LoadingOutlined spin />} style={{ marginTop: 6 }}>
            Downloading GeoLite2 databases from MaxMind…
          </Tag>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// CDNPanel — Google + Cloudflare prefix matcher status & manual refresh
// ---------------------------------------------------------------------------
//
// The matcher itself is owned by sfsensor (which writes the CDN-subset
// spool files). sfconsole holds a read-only second copy for the UI: it
// can show the operator how many prefixes are loaded, when the last
// upstream refresh succeeded, and trigger a manual refresh on demand.
function CDNPanel() {
  const [stats, setStats] = useState<{
    google_count: number;
    cloudflare_count: number;
    last_update: string;
    last_error?: string;
    persist_path?: string;
    auto_update: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await api.cdnStats();
      setStats(s);
    } catch (e) {
      // Endpoint may be unreachable on dev/test consoles without a CDN
      // matcher wired — the panel quietly hides itself in that case.
      setStats(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await load();
      setLoading(false);
    })();
  }, [load]);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await api.cdnRefresh();
      message.success("CDN prefix list refreshed");
      await load();
    } catch (e: any) {
      message.error("Refresh failed: " + (e?.message ?? "unknown"));
    } finally {
      setRefreshing(false);
    }
  }

  async function handleToggleAutoUpdate(checked: boolean) {
    setSavingToggle(true);
    try {
      await api.setCdnAutoUpdate(checked);
      message.success(
        checked
          ? "Daily auto-update enabled"
          : "Daily auto-update disabled — list will stay pinned until you refresh manually"
      );
      await load();
    } catch (e: any) {
      message.error("Failed to update setting: " + (e?.message ?? "unknown"));
    } finally {
      setSavingToggle(false);
    }
  }

  // Pretty-format last_update (RFC3339). Backend sends time.Time.IsZero()
  // as "0001-01-01T00:00:00Z" which we treat as "never".
  function fmtLast(s: string): string {
    if (!s || s.startsWith("0001-")) return "never (using embedded fallback)";
    try {
      return new Date(s).toLocaleString();
    } catch {
      return s;
    }
  }

  return (
    <Card
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <CloudDownloadOutlined />
          <span style={{ fontWeight: 700 }}>CDN Whitelist (Google + Cloudflare)</span>
        </Space>
      }
      extra={
        <Button
          icon={<SyncOutlined spin={refreshing} />}
          onClick={handleRefresh}
          loading={refreshing}
          disabled={!stats}
        >
          Refresh now
        </Button>
      }
    >
      <Paragraph style={{ marginBottom: 12, color: "#666" }}>
        CDN prefix list used to exclude legitimate QUIC/HTTP3 traffic from per-IP rules that
        have <code>exclude_google</code> or <code>exclude_cloudflare</code> enabled. The list
        is automatically refreshed from upstream every 24 hours when daily auto-update is on;
        click "Refresh now" to pull the latest snapshot immediately.
      </Paragraph>
      {loading ? (
        <Text type="secondary">Loading…</Text>
      ) : !stats ? (
        <Alert type="warning" showIcon message="CDN matcher not available — endpoint returned an error or sfconsole has no CDN configured." />
      ) : (
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Google prefixes">
            <Tag color="blue">{stats.google_count}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Cloudflare prefixes">
            <Tag color="orange">{stats.cloudflare_count}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Last refresh" span={2}>
            {fmtLast(stats.last_update)}
          </Descriptions.Item>
          <Descriptions.Item label="Daily auto-update" span={2}>
            <Space>
              <Switch
                checked={stats.auto_update}
                loading={savingToggle}
                onChange={handleToggleAutoUpdate}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {stats.auto_update
                  ? "Enabled — sfsensor pulls upstream lists every 24 hours."
                  : "Disabled — list is pinned to the last snapshot until manually refreshed."}
              </Text>
            </Space>
          </Descriptions.Item>
          {stats.last_error && (
            <Descriptions.Item label="Last error" span={2}>
              <Text type="danger">{stats.last_error}</Text>
            </Descriptions.Item>
          )}
          {stats.persist_path && (
            <Descriptions.Item label="Persist file" span={2}>
              <code style={{ fontSize: 12 }}>{stats.persist_path}</code>
            </Descriptions.Item>
          )}
        </Descriptions>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stateTag(state: UpdateStatus["state"]) {
  switch (state) {
    case "idle":        return <Tag color="default">Idle</Tag>;
    case "downloading": return <Tag color="blue" icon={<LoadingOutlined spin />}>Downloading</Tag>;
    case "applying":    return <Tag color="orange" icon={<SyncOutlined spin />}>Applying</Tag>;
    case "done":        return <Tag color="green" icon={<CheckCircleOutlined />}>Done</Tag>;
    case "error":       return <Tag color="red">Error</Tag>;
    default:            return <Tag>{state}</Tag>;
  }
}

function progressPercent(state: UpdateStatus["state"]): number {
  switch (state) {
    case "downloading": return 40;
    case "applying":    return 75;
    case "done":        return 100;
    default:            return 0;
  }
}

// ---------------------------------------------------------------------------
// Settings page
// ---------------------------------------------------------------------------

export default function Settings() {
  const { isAdmin } = useRole();

  const [checkResult, setCheckResult] = useState<UpdateCheck | null>(null);
  const [checking, setChecking]       = useState(false);

  const [updateStatus, setUpdateStatus] = useState<UpdateStatus | null>(null);
  const [applying, setApplying]         = useState(false);

  // Countdown shown after "done" state to auto-reload
  const [countdown, setCountdown]       = useState<number | null>(null);

  // Interval ref for status polling
  const pollRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const countRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const errCountRef  = useRef(0); // consecutive poll failures → services restarting

  // ---------------------------------------------------------------------------
  // Check for updates
  // ---------------------------------------------------------------------------

  const doCheck = useCallback(async () => {
    setChecking(true);
    try {
      const result = await api.updateCheck();
      setCheckResult(result);
    } catch (e) {
      if (e instanceof Error) message.error("Update check failed: " + e.message);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    doCheck();
  }, [doCheck]);

  // ---------------------------------------------------------------------------
  // Status polling
  // ---------------------------------------------------------------------------

  const triggerReloadCountdown = useCallback((sec = 15) => {
    clearInterval(pollRef.current!);
    pollRef.current = null;
    setApplying(false);
    setCountdown(sec);
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    errCountRef.current = 0;
    pollRef.current = setInterval(async () => {
      try {
        const st = await api.updateStatus();
        errCountRef.current = 0;
        setUpdateStatus(st);

        if (st.state === "done") {
          // sfupdater finished before restarting itself
          triggerReloadCountdown(15);
        } else if (st.state === "idle" || st.state === "error") {
          // sfupdater restarted (fresh state = update done) or fatal error
          clearInterval(pollRef.current!);
          pollRef.current = null;
          setApplying(false);
          if (st.state === "idle") {
            // sfupdater just rebooted → update complete
            setUpdateStatus({ state: "done", message: "Updated successfully!", version: "" });
            setCountdown(5);
          }
        }
      } catch {
        // sfconsole is down (being restarted by sfupdater) — count failures
        errCountRef.current += 1;
        if (errCountRef.current >= 3) {
          // Services are restarting — start countdown
          setUpdateStatus({ state: "applying", message: "Restarting services…", version: "" });
          triggerReloadCountdown(20);
        }
      }
    }, 2000);
  }, [triggerReloadCountdown]);

  // Countdown + auto-reload
  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      window.location.reload();
      return;
    }
    countRef.current = setInterval(() => {
      setCountdown((c) => (c !== null ? c - 1 : null));
    }, 1000);
    return () => {
      if (countRef.current) clearInterval(countRef.current);
    };
  }, [countdown]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (countRef.current) clearInterval(countRef.current);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Apply update
  // ---------------------------------------------------------------------------

  const doApply = async () => {
    setApplying(true);
    setUpdateStatus({ state: "downloading", message: "Starting update…", version: checkResult?.latest ?? "" });
    try {
      await api.updateApply();
      message.success("Update started — monitoring progress…");
      startPolling();
    } catch {
      // Network error here is EXPECTED: sfupdater kills sfconsole before the
      // HTTP response can return. Treat it as "update is running" and start
      // a countdown to reload after services restart.
      setUpdateStatus({ state: "applying", message: "Restarting services…", version: checkResult?.latest ?? "" });
      triggerReloadCountdown(20);
    }
  };

  // Also fetch current status on mount so we don't miss an in-progress update
  useEffect(() => {
    (async () => {
      try {
        const st = await api.updateStatus();
        setUpdateStatus(st);
        if (st.state === "downloading" || st.state === "applying") {
          setApplying(true);
          startPolling();
        }
      } catch {
        // sfupdater might not be installed in dev — silently ignore
      }
    })();
  }, [startPolling]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const isRunning = applying || updateStatus?.state === "downloading" || updateStatus?.state === "applying";

  return (
    <div style={{ maxWidth: 1100 }}>
      <Title level={5} style={{ marginTop: 0, marginBottom: 16, color: "#2a3545" }}>
        System Settings
      </Title>

      {/* ── License panel ────────────────────────────────────────────────── */}
      <LicensePanel />

      {/* ── Update panel ────────────────────────────────────────────────── */}
      <Card
        size="small"
        title={
          <Space>
            <CloudDownloadOutlined />
            <span style={{ fontWeight: 700 }}>Software Updates</span>
            {checkResult?.has_update && (
              <Badge count="Update available" style={{ backgroundColor: "#1677ff" }} />
            )}
          </Space>
        }
        extra={
          <Button
            size="small"
            icon={<SyncOutlined spin={checking} />}
            onClick={doCheck}
            loading={checking}
          >
            Check now
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {/* Version info */}
        {checkResult && (
          <Descriptions size="small" column={2} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Current version">
              <Text code>{checkResult.current}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Latest version">
              <Text code>{checkResult.latest}</Text>
              {checkResult.has_update && (
                <Tag color="blue" style={{ marginLeft: 8 }}>New</Tag>
              )}
            </Descriptions.Item>
            {checkResult.released_at && (
              <Descriptions.Item label="Released">
                {new Date(checkResult.released_at).toLocaleDateString()}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}

        {/* Changelog */}
        {checkResult?.changelog && (
          <Alert
            type="info"
            showIcon
            message="Changelog"
            description={checkResult.changelog}
            style={{ marginBottom: 12 }}
          />
        )}

        {/* Up to date message */}
        {checkResult && !checkResult.has_update && (
          <Alert
            type="success"
            showIcon
            message="You are running the latest version."
            style={{ marginBottom: 12 }}
          />
        )}

        {/* Apply button — admin only */}
        {isAdmin && checkResult?.has_update && !isRunning && countdown === null && (
          <Button
            type="primary"
            icon={<CloudDownloadOutlined />}
            onClick={doApply}
            loading={applying}
          >
            Apply Update to {checkResult.latest}
          </Button>
        )}

        {/* Progress while running */}
        {updateStatus && isRunning && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              {stateTag(updateStatus.state)}
              <Text type="secondary" style={{ fontSize: 12 }}>{updateStatus.message}</Text>
            </div>
            <Progress
              percent={progressPercent(updateStatus.state)}
              status={updateStatus.state === "error" ? "exception" : "active"}
              size="small"
            />
          </div>
        )}

        {/* Done / error state */}
        {updateStatus && !isRunning && updateStatus.state !== "idle" && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              {stateTag(updateStatus.state)}
              <Text type="secondary" style={{ fontSize: 12 }}>{updateStatus.message}</Text>
            </div>
            {updateStatus.state === "done" && countdown !== null && (
              <Alert
                type="success"
                showIcon
                message={`Update complete! Reloading in ${countdown}s…`}
              />
            )}
            {updateStatus.state === "error" && (
              <Alert
                type="error"
                showIcon
                message="Update failed"
                description={updateStatus.message}
              />
            )}
          </div>
        )}

        {!isAdmin && checkResult?.has_update && (
          <Alert
            type="warning"
            showIcon
            message="An update is available. Ask an admin to apply it."
            style={{ marginTop: 8 }}
          />
        )}
      </Card>

      {/* ── Geo / traffic classification toggles ────────────────────────── */}
      <GeoSettingsPanel />

      {/* ── GeoIP database update ────────────────────────────────────────── */}
      <GeoIPUpdatePanel />

      {/* ── CDN whitelist (Google + Cloudflare) ───────────────────────────── */}
      <CDNPanel />

      {/* ── Live top rolling window ───────────────────────────────────────── */}
      <LiveConfigPanel />

      {/* ── External API panels (admin only) ─────────────────────────────── */}
      {isAdmin && (
        <>
          <APIKeysPanel />
          <APIConfigPanel />
        </>
      )}
    </div>
  );
}
